'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { POD_PALETTE, isInternalPod } from './types';

type Datum = string | number | Date | null;
type HoverHandle = {
  vertical?: HTMLElement;
  horizontal?: HTMLElement;
  timestamp?: HTMLElement;
  onCursor?: (timestampMs: number | null) => void;
};

export function formatCursorTimestamp(timestampMs: number): string {
  return new Date(timestampMs).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}

// Imperative cross-chart zoom: each chart registers its plotly graphDiv on
// init, and broadcasts relayout events to the others via Plotly.relayout().
// We deliberately keep the x-range OUT of React state so re-renders never
// fight the user's live zoom — same pattern as the legacy script.
export interface ZoomBus {
  register: (
    id: string,
    el: HTMLElement,
    onRangeChange?: (range: [Datum, Datum] | null) => void,
  ) => void;
  unregister: (id: string) => void;
  // Called by the chart that originated the relayout. `update` is the
  // Plotly relayout patch (e.g. {'xaxis.range[0]': ..., 'xaxis.range[1]': ...}
  // or {'xaxis.autorange': true}).
  broadcast: (originId: string, update: Record<string, unknown>, notifyPage?: boolean) => void;
  // Latest broadcast range, so newly-mounted charts (e.g. a section that was
  // collapsed during the user's zoom) can adopt it on register.
  getCurrentRange: () => [Datum, Datum] | null;
}

export interface HoverBus {
  register: (id: string, handle: HoverHandle) => void;
  unregister: (id: string) => void;
  broadcast: (xRatio: number, yRatio: number, timestampMs?: number | null) => void;
  clear: () => void;
  pin: () => void;
  unpin: () => void;
  isPinned: () => boolean;
  leaveChart: () => void;
  enterChart: () => void;
}

interface ChartContextValue {
  // Color map for pods. Stable across renders for a given run-time pod set.
  podColors: Record<string, string>;

  // Hidden pods — the shared toolbar legend toggles entries here, every chart
  // reads it to set per-trace visibility.
  hiddenPods: Set<string>;
  setHidden: (pod: string, hidden: boolean) => void;
  // Hide all except `pod` (dbl-click "isolate"). If only `pod` is visible
  // already, restore everyone.
  isolatePod: (pod: string) => void;
  hideAll: () => void;
  showAll: () => void;
  setHiddenSet: (pods: Set<string>) => void;

  // Pods register themselves so the legend stays in sync with the data.
  registerPods: (pods: string[]) => void;

  // Cross-chart imperative zoom sync.
  zoomBus: ZoomBus;
  // Cross-chart cursor sync, expressed as a position within each plot area.
  hoverBus: HoverBus;
}

const ChartContext = createContext<ChartContextValue | null>(null);

export function useChartContext(): ChartContextValue {
  const ctx = useContext(ChartContext);
  if (!ctx) throw new Error('useChartContext must be used inside <ChartProvider>');
  return ctx;
}

export function parsePlotlyDateToMs(dateStr: string): number {
  // Plotly emits "YYYY-MM-DD HH:MM:SS"; Date.parse needs T separator.
  return Date.parse(String(dateStr).replaceAll(' ', 'T'));
}

export function buildOnZoomRangeCall(
  update: Record<string, unknown>,
  onZoomRange: (fromMs: number, toMs: number) => void,
): void {
  if (update['xaxis.autorange'] === true) return;
  const r0 = update['xaxis.range[0]'];
  const r1 = update['xaxis.range[1]'];
  if (r0 === undefined || r1 === undefined) return;
  const from = parsePlotlyDateToMs(String(r0));
  const to = parsePlotlyDateToMs(String(r1));
  if (isNaN(from) || isNaN(to)) return;
  onZoomRange(from, to);
}

// Sort pods so internal- pods come first (legacy ordering), then alphabetic.
function sortPods(pods: string[]): string[] {
  return [...pods].sort((a, b) => {
    const ai = isInternalPod(a) ? 0 : 1;
    const bi = isInternalPod(b) ? 0 : 1;
    if (ai !== bi) return ai - bi;
    return a.localeCompare(b);
  });
}

// Plotly is loaded lazily by the charts; we grab a reference once any chart
// has finished mounting so relayout calls work without bundling Plotly here.
import type { Layout } from 'plotly.js';

let plotlyRef: typeof import('plotly.js-basic-dist-min').default | null = null;
async function getPlotly() {
  if (plotlyRef) return plotlyRef;
  const mod = await import('plotly.js-basic-dist-min');
  plotlyRef = mod.default;
  return plotlyRef;
}

export function ChartProvider({
  children,
  onZoomRange,
  initialHiddenPods = [],
}: {
  children: ReactNode;
  onZoomRange?: (fromMs: number, toMs: number) => void;
  initialHiddenPods?: string[];
}) {
  const [knownPods, setKnownPods] = useState<string[]>([]);
  const [hiddenPods, setHiddenPodsState] = useState<Set<string>>(
    () => new Set(initialHiddenPods),
  );

  const podColors = useMemo(() => {
    const map: Record<string, string> = {};
    knownPods.forEach((pod, i) => {
      map[pod] = POD_PALETTE[i % POD_PALETTE.length];
    });
    return map;
  }, [knownPods]);

  const knownSetRef = useRef<Set<string>>(new Set());
  const registerPods = useCallback((pods: string[]) => {
    let changed = false;
    for (const p of pods) {
      if (!knownSetRef.current.has(p)) {
        knownSetRef.current.add(p);
        changed = true;
      }
    }
    if (changed) setKnownPods(sortPods(Array.from(knownSetRef.current)));
  }, []);

  const setHidden = useCallback((pod: string, hidden: boolean) => {
    setHiddenPodsState((prev) => {
      const next = new Set(prev);
      if (hidden) next.add(pod); else next.delete(pod);
      return next;
    });
  }, []);

  const isolatePod = useCallback((pod: string) => {
    setHiddenPodsState((prev) => {
      const allPods = Array.from(knownSetRef.current);
      const onlyThisVisible = prev.size === allPods.length - 1 && !prev.has(pod);
      if (onlyThisVisible) return new Set();
      const next = new Set(allPods.filter((p) => p !== pod));
      return next;
    });
  }, []);

  const hideAll = useCallback(() => setHiddenPodsState(new Set(knownSetRef.current)), []);
  const showAll = useCallback(() => setHiddenPodsState(new Set()), []);
  const setHiddenSet = useCallback((pods: Set<string>) => setHiddenPodsState(new Set(pods)), []);

  // Registry of mounted plot DOM elements, keyed by chart id.
  const plotsRef = useRef<Map<string, HTMLElement>>(new Map());
  const rangeListenersRef = useRef<Map<string, (range: [Datum, Datum] | null) => void>>(new Map());
  // Lightweight DOM overlays avoid Plotly hover-event feedback loops.
  const hoverHandlesRef = useRef<Map<string, HoverHandle>>(new Map());
  const cursorPinnedRef = useRef(false);
  const releaseOnEnterRef = useRef(false);
  const lastHoverRef = useRef<{ xRatio: number; yRatio: number; timestampMs: number | null } | null>(null);
  // Suppress relayout echoes from charts we just relayout()ed programmatically.
  const echoSuppressRef = useRef<Set<string>>(new Set());
  // Latest broadcast range, applied to charts that mount after the user zoomed.
  const currentRangeRef = useRef<[Datum, Datum] | null>(null);

  const onZoomRangeRef = useRef(onZoomRange);
  useEffect(() => { onZoomRangeRef.current = onZoomRange; }, [onZoomRange]);

  const zoomBus: ZoomBus & { _isSuppressed: (id: string) => boolean } = useMemo(() => ({
    register: (id, el, onRangeChange) => {
      plotsRef.current.set(id, el);
      if (onRangeChange) rangeListenersRef.current.set(id, onRangeChange);
      // Adopt the current shared range so a chart that just mounted (e.g.
      // a section that was collapsed during the user's zoom) jumps to the
      // same window as everyone else.
      const range = currentRangeRef.current;
      if (range) {
        void (async () => {
          const Plotly = await getPlotly();
          echoSuppressRef.current.add(id);
          try {
            await Plotly.relayout(el, {
              'xaxis.range[0]': range[0],
              'xaxis.range[1]': range[1],
            } as Partial<Layout>);
          } catch {
            // Plot may have unmounted again; ignore.
          }
          window.setTimeout(() => echoSuppressRef.current.delete(id), 0);
        })();
      }
    },
    unregister: (id) => {
      plotsRef.current.delete(id);
      rangeListenersRef.current.delete(id);
    },
    broadcast: (originId, update, notifyPage = true) => {
      // Track the latest range so newly-registered charts adopt it.
      let nextRange = currentRangeRef.current;
      if (update['xaxis.autorange']) {
        nextRange = null;
      } else if (update['xaxis.range[0]'] !== undefined && update['xaxis.range[1]'] !== undefined) {
        nextRange = [update['xaxis.range[0]'] as Datum, update['xaxis.range[1]'] as Datum];
      }
      currentRangeRef.current = nextRange;
      for (const listener of rangeListenersRef.current.values()) listener(nextRange);
      // Notify page-level zoom handler so it can refetch with the new bounds.
      if (notifyPage && onZoomRangeRef.current) {
        buildOnZoomRangeCall(update, onZoomRangeRef.current);
      }
      void (async () => {
        const Plotly = await getPlotly();
        for (const [id, el] of plotsRef.current) {
          if (id === originId) continue;
          echoSuppressRef.current.add(id);
          try {
            await Plotly.relayout(el, update as Partial<Layout>);
          } catch {
            // Plot may have been unmounted; ignore.
          }
          // Echo from this programmatic call lands in the next microtask;
          // we clear the suppression flag a tick later so user-initiated
          // zooms after this still propagate.
          window.setTimeout(() => echoSuppressRef.current.delete(id), 0);
        }
      })();
    },
    getCurrentRange: () => currentRangeRef.current,
    _isSuppressed: (id: string) => echoSuppressRef.current.has(id),
  }), []);

  const hoverBus: HoverBus = useMemo(() => {
    const showCursor = (xRatio: number, yRatio: number, timestampMs: number | null) => {
      const clampedXRatio = Math.max(0, Math.min(1, xRatio));
      const left = `${clampedXRatio * 100}%`;
      const top = `${Math.max(0, Math.min(1, yRatio)) * 100}%`;
      for (const { vertical, horizontal, timestamp, onCursor } of hoverHandlesRef.current.values()) {
        if (vertical) {
          vertical.style.left = left;
          vertical.style.display = 'block';
        }
        if (horizontal) {
          horizontal.style.top = top;
          horizontal.style.display = 'block';
        }
        if (timestamp) {
          if (timestampMs === null) {
            timestamp.style.display = 'none';
          } else {
            timestamp.style.left = left;
            timestamp.style.transform = clampedXRatio < 0.15
              ? 'translateX(0)'
              : clampedXRatio > 0.85 ? 'translateX(-100%)' : 'translateX(-50%)';
            timestamp.textContent = formatCursorTimestamp(timestampMs);
            timestamp.style.display = 'block';
          }
        }
        onCursor?.(timestampMs);
      }
    };
    const hideCursor = () => {
      for (const { vertical, horizontal, timestamp, onCursor } of hoverHandlesRef.current.values()) {
        if (vertical) vertical.style.display = 'none';
        if (horizontal) horizontal.style.display = 'none';
        if (timestamp) timestamp.style.display = 'none';
        onCursor?.(null);
      }
    };
    return {
      register: (id, handle) => {
        hoverHandlesRef.current.set(id, handle);
        const lastHover = lastHoverRef.current;
        if (cursorPinnedRef.current && lastHover) {
          showCursor(lastHover.xRatio, lastHover.yRatio, lastHover.timestampMs);
        }
      },
      unregister: (id) => { hoverHandlesRef.current.delete(id); },
      broadcast: (xRatio, yRatio, timestampMs = null) => {
        if (cursorPinnedRef.current) return;
        lastHoverRef.current = { xRatio, yRatio, timestampMs };
        showCursor(xRatio, yRatio, timestampMs);
      },
      clear: () => {
        if (cursorPinnedRef.current) return;
        hideCursor();
      },
      pin: () => {
        cursorPinnedRef.current = true;
        releaseOnEnterRef.current = false;
        const lastHover = lastHoverRef.current;
        if (lastHover) showCursor(lastHover.xRatio, lastHover.yRatio, lastHover.timestampMs);
      },
      unpin: () => {
        if (!cursorPinnedRef.current) return;
        cursorPinnedRef.current = false;
        releaseOnEnterRef.current = false;
        hideCursor();
      },
      isPinned: () => cursorPinnedRef.current,
      leaveChart: () => {
        if (cursorPinnedRef.current) {
          releaseOnEnterRef.current = true;
          return;
        }
        hideCursor();
      },
      enterChart: () => {
        if (!cursorPinnedRef.current || !releaseOnEnterRef.current) return;
        cursorPinnedRef.current = false;
        releaseOnEnterRef.current = false;
        hideCursor();
      },
    };
  }, []);

  const value = useMemo<ChartContextValue>(() => ({
    podColors,
    hiddenPods,
    setHidden,
    isolatePod,
    hideAll,
    showAll,
    setHiddenSet,
    registerPods,
    zoomBus,
    hoverBus,
  }), [podColors, hiddenPods, setHidden, isolatePod, hideAll, showAll, setHiddenSet, registerPods, zoomBus, hoverBus]);

  return <ChartContext.Provider value={value}>{children}</ChartContext.Provider>;
}

export { sortPods };
